/**
 * ExecutionContext - React context for flow execution
 * 
 * Provides execution state and controls to React components.
 */

import React, { createContext, useContext, useCallback, useState, useEffect, useMemo } from 'react';
import { useReactFlow } from '@xyflow/react';
import { 
  executionController, 
  ExecutionState, 
  ExecutionOptions, 
  ExecutionMode,
  NodeExecutionStatus,
  NodeExecutionState
} from '../services/ExecutionController';

// Context value type
export interface ExecutionContextValue {
  // State
  state: ExecutionState;
  isExecuting: boolean;
  isPaused: boolean;
  progress: number;
  currentNodeId: string | null;
  
  // Actions
  runFullFlow: () => Promise<void>;
  runFromNode: (nodeId: string) => Promise<void>;
  runSelectedNodes: (nodeIds: string[]) => Promise<void>;
  runSegment: (startNodeId: string, endNodeId: string) => Promise<void>;
  pause: () => void;
  resume: () => void;
  cancel: () => void;
  reset: () => void;
  
  // Helpers
  getNodeStatus: (nodeId: string) => NodeExecutionStatus | undefined;
  canExecute: boolean;
}

// Create context
const ExecutionContext = createContext<ExecutionContextValue | null>(null);

// Provider props
interface ExecutionProviderProps {
  children: React.ReactNode;
}

/**
 * ExecutionProvider - Wraps the app to provide execution context
 */
export function ExecutionProvider({ children }: ExecutionProviderProps) {
  const { getNodes, getEdges } = useReactFlow();
  const [state, setState] = useState<ExecutionState>(executionController.getState());
  
  // Subscribe to execution state changes
  useEffect(() => {
    const unsubscribe = executionController.subscribe((newState) => {
      setState(newState);
    });
    
    return unsubscribe;
  }, []);
  
  // Execute with options
  const execute = useCallback(async (options: ExecutionOptions) => {
    const nodes = getNodes();
    const edges = getEdges();
    await executionController.executeFlow(nodes, edges, options);
  }, [getNodes, getEdges]);
  
  // Run full flow
  const runFullFlow = useCallback(async () => {
    await execute({ mode: 'full', parallel: true });
  }, [execute]);

  // Run from a specific node
  const runFromNode = useCallback(async (nodeId: string) => {
    await execute({ mode: 'from-node', startNodeId: nodeId, parallel: true });
  }, [execute]);

  // Run selected nodes only
  const runSelectedNodes = useCallback(async (nodeIds: string[]) => {
    await execute({ mode: 'selected', selectedNodeIds: nodeIds, parallel: true });
  }, [execute]);

  // Run a segment between two nodes
  const runSegment = useCallback(async (startNodeId: string, endNodeId: string) => {
    await execute({ mode: 'segment', startNodeId, endNodeId, parallel: true });
  }, [execute]);
  
  // Control functions
  const pause = useCallback(() => {
    executionController.pause();
  }, []);
  
  const resume = useCallback(() => {
    executionController.resume();
  }, []);
  
  const cancel = useCallback(() => {
    executionController.cancel();
  }, []);
  
  const reset = useCallback(() => {
    executionController.reset();
  }, []);
  
  // Get status for a specific node
  const getNodeStatus = useCallback((nodeId: string): NodeExecutionStatus | undefined => {
    return state.nodeStates.get(nodeId)?.status;
  }, [state.nodeStates]);
  
  // Can we execute? (not currently running)
  const canExecute = !state.isExecuting;
  
  // Memoize context value
  const value = useMemo<ExecutionContextValue>(() => ({
    state,
    isExecuting: state.isExecuting,
    isPaused: state.isPaused,
    progress: state.progress,
    currentNodeId: state.currentNodeId,
    runFullFlow,
    runFromNode,
    runSelectedNodes,
    runSegment,
    pause,
    resume,
    cancel,
    reset,
    getNodeStatus,
    canExecute,
  }), [
    state,
    runFullFlow,
    runFromNode,
    runSelectedNodes,
    runSegment,
    pause,
    resume,
    cancel,
    reset,
    getNodeStatus,
    canExecute,
  ]);
  
  return (
    <ExecutionContext.Provider value={value}>
      {children}
    </ExecutionContext.Provider>
  );
}

/**
 * useExecution - Hook to access execution context
 */
export function useExecution(): ExecutionContextValue {
  const context = useContext(ExecutionContext);
  
  if (!context) {
    throw new Error('useExecution must be used within an ExecutionProvider');
  }
  
  return context;
}

/**
 * useExecutionState - Hook for just the state (for components that only need to read)
 */
export function useExecutionState(): ExecutionState {
  const { state } = useExecution();
  return state;
}

/**
 * useNodeExecutionStatus - Hook to get a specific node's execution status
 */
export function useNodeExecutionStatus(nodeId: string): NodeExecutionStatus | undefined {
  const { getNodeStatus } = useExecution();
  return getNodeStatus(nodeId);
}

// Export types
export type { ExecutionMode, NodeExecutionStatus, NodeExecutionState, ExecutionState };
